// src/components/dashboard/SectorDashboardV2.tsx
/**
 * Sector Dashboard V2 - Redesigned to match styling of SectorComparisonPage,
 * TokenDashboardV2, and TokensScreener.
 * 
 * Uses DashboardLayout for consistent structure.
 * Each view component fetches its own data using useDashboardData hook.
 */

import React, { useMemo, lazy, Suspense } from "react";
import { DashboardLayout } from "@/components/layout";
import { LoadingSpinner } from "@/components/common";
import type { DashboardType } from "@/types/dashboards/dashboardTypes";
import { DASHBOARD_CONFIGS } from "@/types/dashboards/dashboardTypes";
import { useSectorDashboardPrefetch } from "@/hooks/dashboard/useDashboardData";
import { useSectorDashboardState } from "@/hooks/dashboard/useSectorDashboardState";
import type { IconName } from "lucide-react/dynamic";
import { SectorTokenAnalyticsProvider } from "@/contexts/sectorDashboardV2/SectorTokenAnalyticsProvider";
import { useFocusTracking } from "@/hooks/workspace";

// Lazy load view components for better performance
const SectorOverviewView = lazy(() => import("./v2/SectorOverviewView"));
const SectorPerformanceView = lazy(() => import("./v2/SectorPerformanceView"));
const SectorFundamentalsView = lazy(() => import("./v2/SectorFundamentalsView"));
const SectorVolumeView = lazy(() => import("./v2/SectorVolumeView"));
const SectorDerivativesView = lazy(() => import("./v2/SectorDerivativesView"));
const SectorRiskView = lazy(() => import("./v2/SectorRiskView"));

// Tab types and icons
type TabKey = 'overview' | 'performance' | 'fundamentals' | 'volume' | 'derivatives' | 'risk';

const TAB_ICONS: Record<TabKey, IconName> = {
  overview: 'bar-chart-3',
  performance: 'trending-up',
  fundamentals: 'banknote',
  volume: 'activity',
  derivatives: 'sigma',
  risk: 'gauge',
};

interface SectorDashboardV2Props {
  dashboardType: DashboardType;
  /** Hide the header when embedded in another component that provides its own header */
  hideHeader?: boolean;
}

/**
 * Sector Dashboard V2 component using unified DashboardLayout pattern.
 * Provides consistent styling with SectorComparisonPage and TokenDashboardV2.
 * 
 * Each tab view fetches its own data using useDashboardData, which provides
 * token-level data for the specific sector (dashboardType).
 */
const SectorDashboardV2: React.FC<SectorDashboardV2Props> = ({ 
  dashboardType,
  hideHeader = false,
}) => {
  // Prefetch all data for this dashboard type
  useSectorDashboardPrefetch(dashboardType);

  // Get sector configuration
  const config = DASHBOARD_CONFIGS[dashboardType];
  const sectorTitle = config?.displayName ?? "Sector";
  const sectorDescription = config?.description;

  // Track this sector in workspace context
  useFocusTracking({
    type: "sector",
    entityId: dashboardType,
    entityName: sectorTitle,
  });

  // Get available chart data for this sector
  const { availableCharts } = useSectorDashboardState(dashboardType);

  // Define tabs with lazy-loaded components
  // Each view fetches its own data using useDashboardData hook
  const tabs = useMemo(() => {
    return ([
      ["overview", "Overview"],
      ["performance", "Performance"],
      ["fundamentals", "Valuation & Fundamentals"],
      ["volume", "Volume"],
      ["derivatives", "Derivatives"],
      ["risk", "Risk"],
    ] as Array<[TabKey, string]>).map(([key, label]) => ({
      id: key,
      label,
      icon: TAB_ICONS[key],
      component: (
        <Suspense fallback={<div className="flex justify-center items-center min-h-[400px]"><LoadingSpinner size="lg" /></div>}>
          {key === "overview" && (
            <SectorOverviewView
              dashboardType={dashboardType}
              sectorTitle={sectorTitle}
              sectorDescription={sectorDescription}
              availableCharts={availableCharts}
            />
          )}
          {key === "performance" && (
            <SectorPerformanceView dashboardType={dashboardType} />
          )}
          {key === "fundamentals" && (
            <SectorFundamentalsView dashboardType={dashboardType} />
          )}
          {key === "volume" && (
            <SectorVolumeView dashboardType={dashboardType} />
          )}
          {key === "derivatives" && (
            <SectorDerivativesView
              dashboardType={dashboardType}
              availableDerivativesCharts={availableCharts.derivatives}
            />
          )}
          {key === "risk" && (
            <SectorRiskView dashboardType={dashboardType} />
          )}
        </Suspense>
      ),
    }));
  }, [dashboardType, sectorTitle, sectorDescription, availableCharts]);

  return (
    <SectorTokenAnalyticsProvider dashboardType={dashboardType}>
    <DashboardLayout
      {...(!hideHeader && { title: sectorTitle, subtitle: sectorDescription })}
      tabs={tabs}
      defaultTabId="overview"
      useSuspense={false} // We handle Suspense within each tab component
      embedded={hideHeader} // Remove padding when embedded in another layout
    />
    </SectorTokenAnalyticsProvider>
  );
};

export default SectorDashboardV2;

